import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { requestPasswordReset } from "../api/auth";
import AuthShell from "../components/auth/AuthShell";

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
    } catch (error) {
      // Always show the same success state.
    } finally {
      setSubmitted(true);
      setLoading(false);
    }
  }

  return (
    <AuthShell
      background="radial-gradient(circle at top left, rgba(95, 128, 178, 0.18), transparent 32%), radial-gradient(circle at bottom right, rgba(217, 168, 107, 0.2), transparent 36%), linear-gradient(160deg, #eef4fa 0%, #f6efe6 100%)"
      heroBackground="linear-gradient(180deg, rgba(33, 64, 107, 0.96) 0%, rgba(25, 48, 82, 0.94) 100%)"
      formBackground="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(250,252,255,0.98) 100%)"
      heroTone="dark"
      heroBadge="Recovery"
      heroTitle="Get back into your account without friction."
      heroBody="Enter your email and we’ll send a secure reset link. The goal is to get you back in fast, not make recovery harder."
      heroNote="You’ll see the same response whether the email exists or not, so recovery stays private."
      eyebrow="Forgot password"
      title="Reset your access."
      body="We’ll send a password reset link to your email."
      maxWidth={980}
    >
      {submitted ? (
        <Alert severity="success" sx={{ borderRadius: 3 }}>
          If an account exists for this email, you’ll receive a reset link shortly.
        </Alert>
      ) : (
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Stack spacing={0.75}>
              <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700 }}>
                Email
              </Typography>
              <TextField
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                fullWidth
                required
                placeholder="you@example.com"
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.9)",
                  },
                }}
              />
            </Stack>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              size="large"
              disabled={loading || !email.trim()}
              sx={{
                borderRadius: 999,
                py: 1.35,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "0 16px 28px rgba(27, 64, 111, 0.22)",
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: "inherit", mr: 1 }} /> : null}
              {loading ? "Sending link..." : "Send reset link"}
            </Button>
          </Stack>
        </form>
      )}

      <Box
        sx={{
          pt: 2,
          borderTop: "1px solid #e4eaf1",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: "#5f6978" }}>
          Remembered it?
        </Typography>
        <Button
          component={RouterLink}
          to="/login"
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 2,
            fontWeight: 700,
          }}
        >
          Back to login
        </Button>
      </Box>
    </AuthShell>
  );
}

export default ForgotPasswordPage;
